import React, { MouseEvent } from 'react';

import ImmutablePropTypes from 'react-immutable-proptypes';

import { block } from 'bem-cn';
import moment from 'moment';
import 'moment/locale/sv';
import PropTypes from 'prop-types';

import CheckboxMarker from 'components/CheckboxMarker';

import { LANGUAGES, PION_TYPES } from '../../constants';
import useLocalizedStrings from '../../hooks/useLocalizedStrings';
import { CardType, PionType } from '../../type';
import Typography from '../Typography';

import PionBrandContent from './PionContentTypes/PionBrandContent';
import PionNoteContent from './PionContentTypes/PionNoteContent';
import PionPDFContent from './PionContentTypes/PionPDFContent';
import PionPhotoContent from './PionContentTypes/PionPhotoContent';
import PionReceiptContent from './PionContentTypes/PionReceiptContent';
import PionTypePlateContent from './PionContentTypes/PionTypePlateContent';
import PionUrlContent from './PionContentTypes/PionUrlContent';
import PionYoutubeContent from './PionContentTypes/PionYoutubeContent';
import PionDnDOverlay from './PionDnDOverlay';
import PionEditableTitle from './PionEditableTitle';

import './pion.scss';

const pionTypes = {
  [PION_TYPES.NOTE]: PionNoteContent,
  [PION_TYPES.PHOTO]: PionPhotoContent,
  [PION_TYPES.BRAND]: PionBrandContent,
  [PION_TYPES.PDF]: PionPDFContent,
  [PION_TYPES.MANUAL]: PionPDFContent,
  [PION_TYPES.URL]: PionUrlContent,
  [PION_TYPES.YOUTUBE]: PionYoutubeContent,
  [PION_TYPES.TYPE_PLATE]: PionTypePlateContent,
  [PION_TYPES.RECEIPT]: PionReceiptContent,
};

export const translations = {
  [LANGUAGES.EN]: {
    pionTypes: {
      note: 'Note',
      photo: 'Images',
      pdf: 'Document',
      receipt: 'Receipt',
      brand: 'Brand',
      url: 'Link',
      youtube: 'Video',
      typePlate: 'Data plate',
      manual: 'Manual',
    },
    added: 'Added',
    to: 'to',
  },
  [LANGUAGES.SV]: {
    pionTypes: {
      note: 'Anteckning',
      photo: 'Bilder',
      pdf: 'Dokument',
      receipt: 'Kvitto',
      brand: 'Varumärke',
      url: 'Länk',
      youtube: 'Video',
      typePlate: 'Typskylt',
      manual: 'Manuell',
    },
    added: 'Tillagd',
    to: 'till',
  },
};

const b = block('pion');

const COLORED_TAG_TITLES: any = {
  RED: { tagColor: 'red', titles: ['service'] },
  GREEN: { tagColor: 'green', titles: ['order', 'buy', 'köp', 'beställ'] },
  BLUE: { tagColor: 'blue', titles: ['manual', 'guide'] },
  ORANGE: {
    tagColor: 'orange',
    titles: ['troubleshooting', 'felsökning'],
  },
};

export type PionPropsType = {
  pion: PionType;
  placeholderTitle?: string;
  editMode?: boolean;
  selectMode?: boolean;
  isDragging?: boolean;
  hideDate?: boolean;
  dragOver?: boolean;
  lang: string;
  active?: boolean;
  moving?: boolean;
  notInCard?: boolean;
  hideHomeSign?: boolean;
  showInitials?: boolean;
  hasHomerBadge?: boolean;
  isDraggingOver?: boolean;
  onPionClick: () => void;
  onPionTitleClick: () => void;
  onBadgeClick: () => void;
  onImageClick: (type: string) => void;
  onHeaderClick: () => void;
  onFinishEditTitle: (title: string) => void;
  onPionCheckboxToggle: (pion: PionType, isChecked: boolean) => void;
  renderAddPhotoButton: () => React.ReactNode;
};

const Pion = ({
  pion = ImmutablePropTypes.mapContains({
    id: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    title: PropTypes.string,
    cards: PropTypes.array,
  }),
  onPionClick = () => {},
  onPionTitleClick = () => {},
  onImageClick,
  onBadgeClick = () => {},
  onHeaderClick = () => {},
  onFinishEditTitle = () => {},
  onPionCheckboxToggle = () => {},
  editMode = false,
  selectMode = false,
  dragOver,
  isDragging = false,
  lang,
  renderAddPhotoButton,
  active,
  moving,
  notInCard = false,
  hideHomeSign,
  showInitials = true,
  hasHomerBadge = false,
  placeholderTitle,
  hideDate = false,
}: PionPropsType) => {
  const strings: any = useLocalizedStrings(translations, lang);
  const PionContent = pionTypes[pion.get('type')];

  const isPionTitleIncludesColoredTagTitle = (coloredTagTitles: string[]) =>
    coloredTagTitles.some((t) => pion.get('title')?.toLowerCase().includes(t));

  const showColoredTag = () => {
    return (
      [PION_TYPES.PDF, PION_TYPES.URL].includes(pion.get('type')) &&
      Object.keys(COLORED_TAG_TITLES).some((tag) =>
        isPionTitleIncludesColoredTagTitle(COLORED_TAG_TITLES[tag].titles),
      )
    );
  };

  const getPionTagColor = () => {
    for (const tag in COLORED_TAG_TITLES) {
      if (isPionTitleIncludesColoredTagTitle(COLORED_TAG_TITLES[tag].titles)) {
        return COLORED_TAG_TITLES[tag].tagColor;
      }
    }

    return COLORED_TAG_TITLES.BLUE.tagColor;
  };

  if (!PionContent) {
    return null;
  }
  const hasManualsCard = (pion: PionType) => {
    return pion
      .get('cards')
      ?.some(
        (card: CardType) => card.get('title')?.toLowerCase() === 'manuals',
      );
  };
  const getTitle = () => {
    let title = strings.pionTypes[pion.get('type')];
    let font: any = '';
    if (pion.get('type') === 'pdf' && hasManualsCard(pion)) {
      title = strings.pionTypes.manual;
    }
    if (
      notInCard &&
      pion &&
      pion.get('cardObject') &&
      pion.get('cardObject').get('title')
    ) {
      title = pion.get('cardObject').get('title');
      font = Typography.THEME.VARIANTS.CARD_TITLE_NORMAL;
    } else {
      font = Typography.THEME.VARIANTS.PION_TITLE;
    }

    if (placeholderTitle) {
      title = placeholderTitle;
    }

    return (
      <Typography
        component='h3'
        className={b('type', {
          'with-card-info': pion.has('read') && !pion.get('read'),
        }).toString()}
        variant={font}
        data-testid='pion-type'
      >
        {title}
      </Typography>
    );
  };

  const getInitials = () => {
    const user =
      pion && pion.get('homeObject')
        ? pion.getIn(['homeObject', 'users']).first()
        : null;
    let initials = '';
    if (user) {
      const firstName = user.get('firstName');
      const fullLastNameWords =
        user.has('lastName') && user.get('lastName').split(' ');
      const lastName =
        fullLastNameWords && fullLastNameWords[fullLastNameWords.length - 1];
      initials = user && firstName[0] + (lastName ? lastName[0] : '');
    }
    return initials;
  };

  const handleBadgeClick = (event: any) => {
    event.preventDefault();
    event.stopPropagation();
    onBadgeClick();
  };

  const headerClickHandler = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onHeaderClick();
  };

  const titleClickHandler = (event: MouseEvent<HTMLHeadingElement>) => {
    event.stopPropagation();
    onPionTitleClick();
  };

  const contentClickHandler = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onPionClick();
  };

  const getType = (pion: PionType) => {
    const type = pion.get('type');

    const hasManualCard = type === 'pdf' && hasManualsCard(pion);

    return hasManualCard ? 'manual' : type;
  };

  const renderAddedDateInfo = () => {
    if (selectMode) return null;
    if (pion.has('read') && !pion.get('read') && !hideDate) {
      return (
        <Typography
          component='div'
          className={b('card-info', {
            'two-line': hideHomeSign !== undefined && !hideHomeSign,
          }).toString()}
          variant={
            hideHomeSign
              ? Typography.THEME.VARIANTS.PION_TITLE
              : Typography.THEME.VARIANTS.CARD_TAG_MICRO
          }
        >
          <span>
            {strings.added}
            {!hideHomeSign && (
              <>
                <span data-testid='home-label'>
                  {' '}
                  {strings.to} {pion.getIn(['homeObject', 'title'])}
                </span>
                <br />
              </>
            )}
          </span>
          <span> {moment(pion.get('createdAt')).locale(lang).fromNow()}</span>
        </Typography>
      );
    }

    return null;
  };

  const renderUserInitials = () => {
    if (selectMode) return null;
    if (showInitials && !hasHomerBadge && getInitials()) {
      return (
        <Typography
          component='span'
          className={b('badge').toString()}
          data-testid='badge'
          variant={Typography.THEME.VARIANTS.TITLE_MICRO}
          onClick={(ev: any) => handleBadgeClick(ev)}
        >
          {getInitials()}
        </Typography>
      );
    }

    return null;
  };

  const renderHomerLogo = () => {
    if (selectMode || !hasHomerBadge) return null;

    return (
      <span
        data-testid='homer-logo-badge'
        className={b('homer-logo-badge')}
        onClick={(ev) => handleBadgeClick(ev)}
      />
    );
  };

  return (
    <div
      className={b({
        dragging: isDragging,
        active,
        moving,
        elevated: pion && pion.has('read') && !pion.get('read'),
        [`colored-tag-${getPionTagColor()}`]: showColoredTag(),
      })}
      data-pionid={pion.get('id')}
    >
      <div className={b('header')} onClick={headerClickHandler}>
        <div className={b('header-block')}>
          <div className={b('icon', { type: getType(pion) })} />
          {getTitle()}
        </div>
        {renderAddedDateInfo()}
        {renderUserInitials()}
        {renderHomerLogo()}
        {selectMode && (
          <CheckboxMarker
            onToggle={(isChecked) => onPionCheckboxToggle(pion, isChecked)}
          />
        )}
      </div>
      {dragOver && <PionDnDOverlay lang={lang} />}

      {!editMode && pion.get('title') && (
        <Typography
          component='h3'
          className={b('title').toString()}
          data-testid='pion-title'
          variant={Typography.THEME.VARIANTS.CARD_TITLE_NORMAL}
          onClick={titleClickHandler}
        >
          {pion.get('title')}
        </Typography>
      )}

      {editMode && (
        <PionEditableTitle
          title={pion.get('title')}
          onFinishEdit={onFinishEditTitle}
        />
      )}

      <div className={b('content')} onClick={contentClickHandler}>
        <PionContent
          pion={pion}
          lang={lang}
          onImageClick={onImageClick}
          renderAddPhotoButton={renderAddPhotoButton}
        />
      </div>
    </div>
  );
};

export default Pion;
